import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Wallet, ArrowDownCircle, ArrowUpCircle, Users, UserCheck, Trophy } from "lucide-react";
import { api, brl, fmtDate, monthLabel, STAGES, UNITS } from "@/lib/api";

const KpiCard = ({ icon: Icon, label, value, testid, accent = false }) => (
  <div className={`nx-card p-5 ${accent ? "border-nx-yellow/40 yellow-glow" : ""}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</p>
      <Icon className={`w-4 h-4 ${accent ? "text-nx-yellow" : "text-zinc-500"}`} />
    </div>
    <p className={`font-mono text-xl sm:text-2xl font-bold mt-3 ${accent ? "text-nx-yellow" : "text-white"}`} data-testid={`${testid}-value`}>
      {value}
    </p>
  </div>
);

export default function DashboardHome() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-nx-yellow border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const chartData = data.monthly.map((m) => ({ ...m, label: monthLabel(m.month) }));

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow">NX OS</p>
        <h1 className="font-syne text-3xl sm:text-4xl font-extrabold mt-2" data-testid="dashboard-title">Visão Geral</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard icon={Wallet} label="Fluxo de Caixa" value={brl(data.saldo)} testid="financial-kpi-cashflow" accent />
        <KpiCard icon={ArrowUpCircle} label="A Receber" value={brl(data.a_receber)} testid="financial-kpi-receivable" />
        <KpiCard icon={ArrowDownCircle} label="A Pagar" value={brl(data.a_pagar)} testid="financial-kpi-payable" />
        <KpiCard icon={Users} label="Leads Ativos" value={data.leads_ativos} testid="kpi-active-leads" />
        <KpiCard icon={UserCheck} label="Clientes" value={data.clientes} testid="kpi-clients" />
        <KpiCard icon={Trophy} label="Negócios Ganhos" value={data.ganhos} testid="kpi-won-deals" />
      </div>

      {data.alertas && data.alertas.length > 0 && (
        <div className="nx-card p-6 border-amber-500/30" data-testid="dashboard-due-alerts">
          <h2 className="font-syne text-lg font-bold mb-4">Contas vencendo</h2>
          <div className="space-y-3">
            {data.alertas.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-800/60 pb-3 last:border-0 last:pb-0" data-testid={`due-alert-${a.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{a.description}</p>
                  <p className="text-[11px] text-zinc-500">{a.type === "receita" ? "A receber" : "A pagar"} · {fmtDate(a.due_date)}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`font-mono text-xs ${a.type === "receita" ? "text-nx-yellow" : "text-zinc-400"}`}>{brl(a.amount)}</span>
                  <span className={`text-[10px] font-mono uppercase border rounded-full px-2.5 py-1 ${
                    a.dias < 0 ? "text-red-400 border-red-500/40" : a.dias === 0 ? "text-amber-300 border-amber-500/40" : "text-zinc-400 border-zinc-600"
                  }`}>
                    {a.dias < 0 ? `Vencido há ${-a.dias}d` : a.dias === 0 ? "Vence hoje" : `Vence em ${a.dias}d`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="nx-card p-6 lg:col-span-2" data-testid="dashboard-cashflow-chart">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-syne text-lg font-bold">Fluxo de caixa — 6 meses</h2>
            <Link to="/app/financeiro" className="text-xs text-nx-yellow font-semibold hover:underline" data-testid="goto-financial-link">
              Ver financeiro →
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C6F14A" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#C6F14A" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gDesp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#71717A" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#71717A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#272732" />
              <XAxis dataKey="label" stroke="#71717A" fontSize={11} fontFamily="JetBrains Mono" />
              <YAxis stroke="#71717A" fontSize={11} fontFamily="JetBrains Mono" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: "#18181F", border: "1px solid #272732", borderRadius: 12, fontFamily: "JetBrains Mono", fontSize: 12 }}
                formatter={(v) => brl(v)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="receitas" name="Receitas" stroke="#C6F14A" strokeWidth={2} fill="url(#gRec)" />
              <Area type="monotone" dataKey="despesas" name="Despesas" stroke="#71717A" strokeWidth={2} fill="url(#gDesp)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="nx-card p-6" data-testid="dashboard-pipeline-summary">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-syne text-lg font-bold">Pipeline</h2>
            <Link to="/app/crm" className="text-xs text-nx-yellow font-semibold hover:underline" data-testid="goto-crm-link">
              Ver CRM →
            </Link>
          </div>
          <p className="font-mono text-2xl font-bold text-nx-yellow" data-testid="pipeline-value">{brl(data.pipeline_value)}</p>
          <p className="text-[11px] text-zinc-500 font-mono uppercase tracking-widest mt-1">em negociação aberta</p>
          <div className="mt-6 space-y-3">
            {Object.entries(data.by_stage).map(([stage, count]) => (
              <div key={stage} className="flex items-center gap-3">
                <span className="text-xs text-zinc-400 w-24">{STAGES.find((s) => s.id === stage)?.label || stage}</span>
                <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-nx-yellow rounded-full transition-all"
                    style={{ width: `${data.leads_total ? (count / data.leads_total) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-xs font-mono text-zinc-500 w-6 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nx-card p-6" data-testid="dashboard-recent-leads">
          <h2 className="font-syne text-lg font-bold mb-5">Leads recentes</h2>
          <div className="space-y-3">
            {data.recent_leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between border-b border-zinc-800/60 pb-3 last:border-0 last:pb-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{l.name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{l.company} · {UNITS[l.unit]}</p>
                </div>
                <span className="font-mono text-xs text-nx-yellow shrink-0 ml-3">{brl(l.value)}</span>
              </div>
            ))}
            {data.recent_leads.length === 0 && <p className="text-sm text-zinc-500">Nenhum lead ainda.</p>}
          </div>
        </div>
        <div className="nx-card p-6" data-testid="dashboard-recent-transactions">
          <h2 className="font-syne text-lg font-bold mb-5">Últimos lançamentos</h2>
          <div className="space-y-3">
            {data.recent_transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b border-zinc-800/60 pb-3 last:border-0 last:pb-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{t.description}</p>
                  <p className="text-[11px] text-zinc-500">{fmtDate(t.due_date)} · {t.status}</p>
                </div>
                <span className={`font-mono text-xs shrink-0 ml-3 ${t.type === "receita" ? "text-nx-yellow" : "text-zinc-400"}`}>
                  {t.type === "receita" ? "+" : "−"} {brl(t.amount)}
                </span>
              </div>
            ))}
            {data.recent_transactions.length === 0 && <p className="text-sm text-zinc-500">Nenhum lançamento ainda.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
